// src/pages/ServicesPage.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, Search, Zap, ArrowRight, Activity, ArrowUpRight } from "lucide-react";

const services = [
  // ------------------ IT Services ------------------
  {
    title: "Website Development",
    description: "Responsive, modern websites for businesses, portfolios, and industries. Tailored to your needs with SEO optimization.",
    category: "IT Services",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2426&auto=format&fit=crop",
    tags: ["React", "Tailwind", "SEO", "Web Design"],
  },
  {
    title: "Custom IT Tools",
    description: "Automation solutions like NDT report generators, calculators, and inspection logbooks designed for industrial workflows.",
    category: "IT Services",
    image: "https://images.unsplash.com/photo-1534972195531-d756b9bfa9f2?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    tags: ["Automation", "SaaS", "NDT Tools", "Efficiency"],
  },
  {
    title: "Software & SaaS Solutions",
    description: "Cloud platforms, dashboards, and admin portals to streamline inspection, calibration, and data management.",
    category: "IT Services",
    image: "https://images.unsplash.com/photo-1712214354975-a085467dd5e4?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    tags: ["SaaS", "Firebase", "Analytics", "Dashboards"],
  },

  // ------------------ NDT Services ------------------
  {
    title: "Ultrasonic Testing (UT & PAUT)",
    description: "Advanced ultrasonic and phased array inspections for welds, plates, and castings to ensure quality and safety.",
    category: "NDT Services",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1470&auto=format&fit=crop",
    tags: ["UT", "PAUT", "Welds", "Casting"],
  },
  {
    title: "Magnetic Particle Inspection (MPI)",
    description: "Surface flaw detection for welds and ferromagnetic materials using magnetic particle testing.",
    category: "NDT Services",
    image: "https://focusinspectiontechnology.com/static/media/MPT2.e543703513d996466e74.jpg",
    tags: ["MPI", "Surface Inspection", "Weld Joints"],
  },
  {
    title: "Penetrant Testing (PT)",
    description: "Non-ferrous surface defect detection with liquid penetrant testing for critical inspections.",
    category: "NDT Services",
    image: "https://focusinspectiontechnology.com/static/media/LPT14.f31077b2c79b30c1f688.jpg",
    tags: ["PT", "Surface Defects", "Non-Ferrous"],
  },
  {
    title: "Visual Testing (VT)",
    description: "Professional weld and structural inspections with detailed reporting and documentation.",
    category: "NDT Services",
    image: "https://images.unsplash.com/photo-1704085660766-cd132f99afb2?q=80&w=1929&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    tags: ["VT", "Weld Inspection", "Reports"],
  },
];

export default function Services() {
  const [activeTab, setActiveTab] = useState("all");
  const tabs = ["all", "IT Services", "NDT Services"];

  useEffect(() => {
    document.title = "Omkar More – Services | IT & NDT Solutions";
  }, []);

  const filteredServices = services.filter((s) => {
    if (activeTab === "all") return true;
    return s.category === activeTab;
  });

  const categories = filteredServices.reduce((acc, service) => {
    const category = service.category || "Other Services";
    if (!acc[category]) acc[category] = [];
    acc[category].push(service);
    return acc;
  }, {});

  return (
    <div className="bg-slate-50 min-h-screen pb-20 overflow-x-hidden">
      {/* 1. HERO HEADER */}
      <section className="bg-white border-b border-slate-200 pt-28 md:pt-40 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-10">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-cyan-600" />
                <span className="font-mono text-xs tracking-[0.4em] uppercase font-black text-slate-400">
                  Capabilities Matrix
                </span>
              </div>
              <h1 className="text-5xl sm:text-6xl md:text-8xl font-black text-slate-950 tracking-tighter leading-[0.85]">
                Industrial <br />
                <span className="text-cyan-600 font-light italic">Solutions</span>
              </h1>
            </div>
            
            <nav className="flex items-center gap-2 overflow-x-auto pb-4 lg:pb-0 no-scrollbar">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`whitespace-nowrap px-8 py-3 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                    activeTab === tab
                      ? "bg-slate-950 text-white shadow-2xl shadow-slate-400 translate-y-[-2px]"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </section>

      {/* 2. GROUPED SERVICE GRID */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-16 md:mt-24">
        {Object.entries(categories).map(([categoryName, servicesList]) => (
          <div key={categoryName} className="mb-24 last:mb-0">
            {/* Category Sub-Header */}
            <div className="flex items-center gap-6 mb-12">
              <div className="flex flex-col">
                <h3 className="text-3xl md:text-4xl font-black text-slate-950 uppercase tracking-tighter">
                  {categoryName}
                </h3>
                <div className="h-1.5 w-16 bg-cyan-600 mt-3" />
              </div>
              <div className="flex-1 h-px bg-slate-200" />
              <Activity size={20} className="text-slate-300 animate-pulse" />
            </div>

            {/* Grid of services */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {servicesList.map((service, i) => (
                <div
                  key={i}
                  className="bg-white border border-slate-200 rounded-[2.5rem] group hover:border-cyan-300 transition-all duration-500 flex flex-col min-h-[500px] shadow-sm hover:shadow-2xl hover:shadow-cyan-100/50 overflow-hidden"
                >
                  {/* SERVICE IMAGE CONTAINER */}
                  <div className="h-56 w-full overflow-hidden relative">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover  group-hover:scale-110 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-black/20" />
                    
                    {/* Icon Badge */}
                    <div className="absolute top-6 left-6">
                      <div className="p-4 bg-white/90 backdrop-blur-md rounded-2xl shadow-xl">
                        {categoryName.includes("IT") ? (
                          <Cpu size={24} className="text-cyan-600" />
                        ) : (
                          <Search size={24} className="text-cyan-600" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* CONTENT AREA */}
                  <div className="p-8 md:p-10 flex flex-col flex-grow">
                    <h4 className="text-2xl font-black text-slate-950 mb-4 uppercase tracking-tight group-hover:text-cyan-600 transition-colors leading-tight">
                      {service.title}
                    </h4>
                    
                    <p className="text-slate-500 text-sm md:text-base leading-relaxed mb-8 flex-grow">
                      {service.description}
                    </p>

                    {/* TECHNICAL METADATA */}
                    <div className="pt-8 border-t border-slate-100">
                      <div className="flex flex-wrap gap-2 mb-8">
                        {service.tags.map((tag, j) => (
                          <span
                            key={j}
                            className="bg-slate-50 text-slate-500 text-[10px] font-black px-3 py-1.5 rounded-lg uppercase tracking-widest border border-slate-100 group-hover:border-cyan-100 group-hover:text-cyan-700 transition-colors"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div className="flex gap-2">
                           <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse shadow-[0_0_10px_rgba(6,182,212,0.8)]" />
                           <div className="w-2 h-2 rounded-full bg-slate-100 group-hover:bg-cyan-200 transition-colors" />
                           <div className="w-2 h-2 rounded-full bg-slate-100 group-hover:bg-cyan-100 transition-colors" />
                        </div>
                        <div className="flex items-center gap-2 text-slate-300 group-hover:text-cyan-500 transition-colors font-bold text-[10px] uppercase tracking-widest">
                          Deploy <ArrowUpRight size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* 3. CALL TO ACTION */}
        <div className="mt-32 bg-slate-950 rounded-[3rem] md:rounded-[5rem] p-10 md:p-24 text-center relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-cyan-600/20 rounded-full blur-[120px] -mr-64 -mt-64 transition-all duration-1000 group-hover:bg-cyan-600/30" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/10 rounded-full blur-[80px] -ml-32 -mb-32" />
          
          <div className="relative z-10 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8">
              <Zap size={14} className="text-cyan-400 fill-cyan-400" />
              <span className="text-[10px] font-black text-white uppercase tracking-[0.3em]">Project Initiation</span>
            </div>
            <h3 className="text-4xl md:text-7xl font-black text-white mb-8 tracking-tighter uppercase leading-[0.9]">
              Calibrate Your <br /> <span className="text-cyan-500 italic font-light">Workflow</span>
            </h3>
            <p className="text-slate-400 text-lg md:text-xl mb-12 font-medium leading-relaxed">
              Ready to deploy industrial-grade IT solutions or high-precision NDT inspections? Let's discuss your technical requirements.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-6 px-12 py-6 bg-cyan-600 text-white font-black text-xs md:text-sm uppercase tracking-[0.3em] rounded-full hover:bg-white hover:text-slate-950 transition-all duration-500 active:scale-95 shadow-2xl shadow-cyan-900/20 hover:shadow-white/10"
            >
              Contact Me <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}